import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar, MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { dateTimeFacade, dateTimeHelper, EBookingPageType } from '@cf/core';
import { CfSchemaMeetingInfo, ITimeslots } from '@cf/temp/services';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { DateTime } from 'luxon';
import { startWith, timer } from 'rxjs';
import { BookingCalendarCustomHeaderComponent } from '../booking-calendar-custom-header/booking-calendar-custom-header.component';

@Component({
  selector: 'cf-booking-calendar',
  templateUrl: './booking-calendar.component.html',
  styleUrls: ['./booking-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [MatCalendar, NgxTolgeeModule],
})
export class BookingCalendarComponent implements OnInit, OnChanges, AfterViewInit {
  matLuxonDateAdapter = inject(DateAdapter);
  translateService = inject(TranslateService);

  @ViewChild(MatCalendar) matCalendar!: MatCalendar<Date>;

  @Input({ required: true }) timeslots!: ITimeslots[];

  @Input({ required: false }) day!: string | undefined;

  @Input({ required: false }) isTouch!: boolean | null;

  @Input() booking!: CfSchemaMeetingInfo;

  @Input() selected!: Date | null;

  @Input() bookingType: 'booking' | 'reschedule' = 'booking';

  @Input() hasFreeSlots!: boolean;

  @Output() emitSlot: EventEmitter<ITimeslots> = new EventEmitter<ITimeslots>();

  @Output() emitDateRange: EventEmitter<{
    from_date: string;
    to_date: string;
  }> = new EventEmitter();

  hasNext!: boolean;
  hasPrev!: boolean;

  headerComponent = BookingCalendarCustomHeaderComponent;

  readonly EMeetingType = EBookingPageType;

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    const currentCellDate = (
      cellDate instanceof Date ? dateTimeHelper.getDateFromJS(cellDate) : cellDate
    ) as DateTime;
    const currentDate = currentCellDate.toSQLDate() as string;
    const todayDate = dateTimeHelper.dt.now().toSQLDate();
    let classes = '';
    if (view === 'month') {
      if (this.day && this.bookingType === 'reschedule') {
        if (
          dateTimeFacade.getDiffFromISO(this.day, currentDate, 'day').toString().startsWith('0.')
        ) {
          classes = 'selected ';
        }
      }
      // if (this.booking.type === EBookingPageType.GROUP) {
      // classes += currentDate >= todayDate ? 'available' : 'disabled';
      // check here
      // } else {
      classes += this.timeslots.some((t) => {
        if (t.time_slots) {
          return t.date === currentDate;
        } else {
          return t.date === currentDate && t.free_slots?.length && t.date >= todayDate;
        }
      })
        ? 'available'
        : 'disabled';
      // }
    }

    return classes;
  };

  constructor(private readonly renderer: Renderer2) {
    this.translateService.languageAsync
      .pipe(startWith(this.translateService.language), takeUntilDestroyed())
      .subscribe((lang) => {
        this.matLuxonDateAdapter.setLocale(lang);
      });
  }

  ngOnInit() {
    if (this.day) {
      if (!this.isTouch) {
        this.selected = dateTimeHelper.getDateFromISO(this.day).toJSDate();
        this.onChange();
      }
    }
    this.handleShowCalendarArrows();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.timeslots) {
      this.matCalendar?.updateTodaysDate();
    }
  }

  ngAfterViewInit() {
    const BACK_BUTTON = document.querySelector('.calendar-previous-button');
    const FORWARD_BUTTON = document.querySelector('.calendar-next-button');
    if (BACK_BUTTON) {
      this.renderer.listen(BACK_BUTTON, 'click', () => {
        timer(100).subscribe(() => {
          this.handleDateRange(this.matCalendar.monthView.activeDate);
        });
      });
    }
    if (FORWARD_BUTTON) {
      this.renderer.listen(FORWARD_BUTTON, 'click', () => {
        timer(100).subscribe(() => {
          this.handleDateRange(this.matCalendar.monthView.activeDate);
        });
      });
    }
  }

  onChange(date: DateTime | Date = this.selected as Date) {
    date = date instanceof Date ? dateTimeHelper.getDateFromJS(date) : date;
    const currentDate = date.toSQLDate();
    let currentSlot = this.timeslots.find((t) => t.date === currentDate);

    if (!currentSlot) {
      let startDate = dateTimeFacade.startOf('day');
      if (date.hasSame(dateTimeHelper.dt.now(), 'day')) {
        startDate = dateTimeFacade.timeToNearest();
      }
      currentSlot = {
        date: currentDate as string,
        free_slots: [
          {
            start: startDate.toString(),
            end: date.endOf('day').toString(),
          },
        ],
      };
    }
    this.emitSlot.emit(currentSlot);
  }

  handleDateRange(date: DateTime | Date) {
    date = date instanceof Date ? dateTimeHelper.getDateFromJS(date) : date;
    this.emitDateRange.emit({
      from_date: date.startOf('month').toSQLDate() as string,
      to_date: date.endOf('month').toSQLDate() as string,
    });
    this.handleShowCalendarArrows(date);
  }

  handleShowCalendarArrows(currentDate = dateTimeHelper.dt.now()) {
    const today = dateTimeHelper.dt.now();
    this.hasPrev = currentDate.month > today.month;

    if (!this.booking.last_availability) {
      this.hasNext = true;
    } else {
      this.hasNext =
        currentDate.month < dateTimeHelper.getDateFromISO(this.booking.last_availability).month;
    }
  }
}
